import React from 'react';
import axios from 'axios';

import { PaymentMethods, Gateways } from '../constants';
import { getBaseUrl } from '../utils/baseUrl';

interface StripeSettingsResponseParams {
  apiVersion: string;
  account: string;
  clientKey: string;
}

interface AdyenSettingsResponseParams {
  [key: string]: any;
}

export type IsBillingRequredType = 'mandatory' | 'optional';
export type ModeType = 'live' | 'test';

/* eslint-disable @typescript-eslint/naming-convention */
export enum BillingFieldsEnum {
  'first_name',
  'last_name',
  'name',
  'birthday',
  'document_number',
  'document_type',
  'tax_id',
  'address1',
  'address2',
  'city',
  'state',
  'country',
  'zip',
  'company',
  'email',
  'phone',
  'fax',
}
/* eslint-enable @typescript-eslint/naming-convention */

export type BillingSettingsResponseParams = {
  [key in keyof typeof BillingFieldsEnum]?: IsBillingRequredType;
}

export interface SettingsResponseDataParams {
  gateway: typeof Gateways[number];
  stripe: StripeSettingsResponseParams;
  adyen: AdyenSettingsResponseParams;
  billingAddressFields: BillingSettingsResponseParams;
  mode: ModeType;
  paymentMethods: typeof PaymentMethods[number][];
}

export interface SettingsResponseParams {
  success: boolean;
  statusMessage: string;
  data: SettingsResponseDataParams;
  statusCode: 0;
}

const DefaultErrorMessage = 'Failed to retrieve property configuration.';

type SettingsReturnType = [SettingsResponseDataParams | null, boolean, string | null];

const baseUrl = getBaseUrl();
const apiEndpoint = '/payment/PaymentElement/settings';

export function useSettings(token: string, propertyId: number): SettingsReturnType {
  const [data, setData] = React.useState<SettingsResponseDataParams | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data: response } = await axios.post(apiEndpoint, {
          property_id: propertyId,
        }, {
          baseURL: baseUrl,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.success) throw new Error(response.statusMessage || DefaultErrorMessage);

        setData(response.data);
      } catch (err) {
        setError((err as any).message || DefaultErrorMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, propertyId]);

  return [data, loading, error];
}
